// Copyright text placeholder, Warner Bros. Discovery, Inc.

/**
 *  WARNING:
 *  This file is auto-generated. Do not edit manually. Any changes made to this
 *  file will be overwritten the next time it is generated. If you need to make
 *  changes, please modify the source files and re-generate this file.
 */

/**
 * Defines which symbolication format the stack traces from this application are using. Used by Blackbox to deobfuscate stack traces.
 * @public
 * @readonly
 */

enum FormatV1 {
  /** Format of PROGUARD **/
  PROGUARD = 'PROGUARD',
  /** Format of PLCRASH **/
  PLCRASH = 'PLCRASH',
  /** Format of MINIDUMP **/
  MINIDUMP = 'MINIDUMP'
}
/**
 * Nature of the internet connection that the client application had when the request was made
 * @public
 * @readonly
 */

enum ConnectionTypeV1 {
  /** ConnectionType of WIFI **/
  WIFI = 'WIFI',
  /** ConnectionType of WIRED **/
  WIRED = 'WIRED',
  /** ConnectionType of WIRELESS **/
  WIRELESS = 'WIRELESS',
  /** ConnectionType of DISCONNECTED **/
  DISCONNECTED = 'DISCONNECTED',
  /** ConnectionType of NONE **/
  NONE = 'NONE',
  /** ConnectionType of UNSPECIFIED **/
  UNSPECIFIED = 'UNSPECIFIED'
}

/**
 *
 *
 * version 1.0
 * @public
 */
interface IOmdV1 {
  /** Uniquely identifiable name for each sdk and application. One Jira team may be responsible for multiple components. Other OMD information can be inferred from this value. **/
  component: string;
}

/**
 * When present, the symbolication object defines what form of stack trace symbolication the client uses, as well as any metadata needed to perform that deobfuscation. This can be used to deobfuscate stack traces in the data pipeline.
 *
 * version 1.0
 * @public
 */
interface ISymbolicationV1 {
  /** Defines which symbolication format the stack traces from this application are using. Used by Blackbox to deobfuscate stack traces. **/
  format?: FormatV1;
  /** When the format is PROGUARD, this is the ID of the Proguard mapping file used to symbolicate the stack traces. **/
  proguardId?: string;
}

/**
 * Values pertaining to the client application that this event is coming from
 *
 * version 1.0
 * @public
 */
interface IApplicationV1 {
  /** The build number is a number added to the versions above to make them unique, and it is typically a sequentially-increasing number unique to the given version, resetting back to 1 for the next app version. Some platforms cap build number to 999, or 9999, making it clear that you are intended to &quot;wrap back to 1&quot; for each new app version string.
  For applications, the build number is appended to the version string, giving the classic 4-number octet (Major.Minor.Patch.Build). **/
  buildNumber: number;
  /**  **/
  omd: IOmdV1;
  /** A semantic (major, minor, patch) version indicating the &quot;binary client&quot; version, or the binary half of a &quot;split client&quot; version.
  A &quot;binary client&quot; refers to clients distributed to customers as an installable executable through a partner. iOS and AppleTV, Android, AndroidTV, FireTV, and Roku are all examples of binary clients.
  A &quot;split client&quot; is a client that consists of both an installable executable, and a deployed URL. A split client requires an installed executable to operate, although often, most updates to the application once installed can be accomplished with just a new deployment to the web. XBox, PS4/PS5, Tizen, and WebOS are all examples of split clients.
  Injected at build time. This is also the application.version in most situations. **/
  binaryVersion?: string;
  /** A semantic (major, minor, patch) version indicating the &quot;deployed client&quot; version, or the deployed half of the &quot;split client&quot; version.
  A &quot;deployed client&quot; refers to clients accessed by customers via a URL on the web – this might mean the customer accesses a website directly, or that a device (like a TV or set-top box) is designed to access a URL. Web, Chromecast, Comcast, Vizio, LG (and most other Lightning apps) are all examples of deployed clients.
  A &quot;split client&quot; is a client that consists of both an installable executable, and a deployed URL. A split client requires an installed executable to operate, although often, most updates to the application once installed can be accomplished with just a new deployment to the web. XBox, PS4/PS5, Tizen, and WebOS are all examples of split clients.
  Injected at build time. This is used as the application.version when it is present, because this version is more indicative of the user experience when it does appear. **/
  deployedVersion?: string;
  /** The store version is a string that uniquely identifies software distributed to customers by a partner. This version applies to binary clients, and to the binary half of a split client (it does not apply to deployed clients). The “store version” may be visible to the customer on the iOS or Android app stores, or while downloading an update on a game console, for example.
  In general, it is helpful if the store version is the same as the app version. However, the store version pec is controlled by the partner, and so it may have unique constraints (e.g. Apple, Microsoft, Sony) that mean it does not always match exactly. **/
  storeVersion?: string;
  /** When present, the symbolication object defines what form of stack trace symbolication the client uses, as well as any metadata needed to perform that deobfuscation. This can be used to deobfuscate stack traces in the data pipeline. **/
  symbolication?: ISymbolicationV1;
}

/**
 *
 *
 * version 1.0
 * @public
 */
interface INetworkV1 {
  /** Nature of the internet connection that the client application had when the request was made **/
  connectionType: ConnectionTypeV1;
}

/**
 *
 *
 * version 1.0
 * @public
 */
interface IApplicationStateV1 {
  /**  **/
  network: INetworkV1;
  /** A client-driven UUID which may be used to group events coming from the same client device during the same period of client activity **/
  sessionId: string;
}

/**
 *
 *
 * version 1.0
 * @public
 */
interface IBrowserV1 {
  /** The name of the web browser which is accessing the web client application **/
  name?: string;
  /** The version of the web browser which is accessing the web client application **/
  version?: string;
}

/**
 *
 *
 * version 1.0
 * @public
 */
interface IDeviceV1 {
  /**  **/
  browser?: IBrowserV1;
  /** The manufacturer defined grouping of similar hardware characteristics. While the &#x27;device.platform&#x27; groups a whole family of devices, chipset is for grouping hardware characterics within that platform. This helps identify issues which only affect a particular line of device. **/
  chipset?: string;
  /** The version of the firmware running on the device as provided by the platform holder **/
  firmwareVersion?: string;
}

/**
 * Schema whose fields are contained in every event
 *
 * version 1.0
 * @public
 */
export interface IGlobalContextV1 {
  /** Values pertaining to the client application that this event is coming from **/
  application: IApplicationV1;
  /**  **/
  applicationState: IApplicationStateV1;
  /**  **/
  device?: IDeviceV1;
}

/**
 * Schema whose fields are contained in every event
 * @public
 */
export interface IGlobalContextV1Props extends Omit<IGlobalContextV1, 'applicationState'> {
  /** {@inheritdoc IGlobalContextV1."applicationState"} */
  applicationState: Omit<IGlobalContextV1['applicationState'], 'sessionId'>;
}

/**
 * Schema whose fields are contained in every event
 * @public
 */
export class GlobalContextV1 {
  /** Values pertaining to the client application that this event is coming from */
  public readonly application: IGlobalContextV1Props['application'];
  /**  */
  public readonly applicationState: IGlobalContextV1Props['applicationState'];
  /**  */
  public readonly device?: IGlobalContextV1Props['device'];
  public static FORMAT: typeof FormatV1 = FormatV1;
  public static CONNECTION_TYPE: typeof ConnectionTypeV1 = ConnectionTypeV1;

  /** The current context type/version we are using from instrumentation-schemas */
  public readonly contextType: string = 'beam.context.global_1.0';

  public constructor(props: IGlobalContextV1Props) {
    this.application = props.application;
    this.applicationState = props.applicationState;
    this.device = props.device;
  }
}

export { FormatV1 as GlobalContextV1Format };
export { ConnectionTypeV1 as GlobalContextV1ConnectionType };
export type { IOmdV1 as IGlobalContextV1Omd };
export type { ISymbolicationV1 as IGlobalContextV1Symbolication };
export type { IApplicationV1 as IGlobalContextV1Application };
export type { INetworkV1 as IGlobalContextV1Network };
export type { IApplicationStateV1 as IGlobalContextV1ApplicationState };
export type { IBrowserV1 as IGlobalContextV1Browser };
export type { IDeviceV1 as IGlobalContextV1Device };
